import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { signInWithPopup } from "firebase/auth";
import { authenticate, googleProvider } from "../utils/firebase";
import { Lock } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: linear-gradient(135deg, #0a0f1c 0%, #1a1f3c 50%, #0a0f1c 100%);
  position: relative;
  overflow: hidden;
`;

const BackgroundOrb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  animation: ${pulse} 4s ease-in-out infinite;

  &:nth-child(1) {
    width: 400px; height: 400px;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    top: -100px; left: -100px; opacity: 0.3;
  }
  &:nth-child(2) {
    width: 300px; height: 300px;
    background: linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%);
    bottom: -50px; right: -50px; opacity: 0.25;
    animation-delay: 2s;
  }
  &:nth-child(3) {
    width: 200px; height: 200px;
    background: linear-gradient(135deg, #06b6d4 0%, #6366f1 100%);
    top: 40%; right: 20%; opacity: 0.2;
    animation-delay: 1s;
  }
`;

const LeftSection = styled.div`
  flex: 1; display: flex; flex-direction: column;
  justify-content: center; align-items: center;
  padding: 48px; position: relative; z-index: 1;
  @media (max-width: 900px) { display: none; }
`;

const BrandSection = styled.div`text-align: center;`;

const LogoWrapper = styled.div`
  display: flex; align-items: center; justify-content: center;
  gap: 16px; margin-bottom: 32px;
  animation: ${float} 6s ease-in-out infinite;
`;

const LogoIcon = styled.div`
  width: 72px; height: 72px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 18px; display: flex;
  align-items: center; justify-content: center;
  font-weight: 700; font-size: 32px; color: white;
  box-shadow: 0 20px 40px rgba(99, 102, 241, 0.4);
`;

const BrandName = styled.h1`
  font-size: 48px; font-weight: 700; color: #f8fafc; letter-spacing: -1px;
`;

const TagLine = styled.p`
  font-size: 18px; color: #94a3b8; max-width: 400px; line-height: 1.6;
`;

const RightSection = styled.div`
  flex: 1; display: flex; align-items: center; justify-content: center;
  padding: 48px; position: relative; z-index: 1;
`;

const LoginCard = styled.div`
  width: 100%; max-width: 420px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px); border-radius: 24px;
  padding: 48px 40px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
`;

const CardTitle = styled.h2`
  font-size: 28px; font-weight: 600; color: #f8fafc;
  margin-bottom: 8px; text-align: center;
`;

const CardSubtitle = styled.p`
  font-size: 14px; color: #94a3b8; text-align: center; margin-bottom: 36px;
`;

const GoogleButton = styled.button`
  width: 100%; padding: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px; font-size: 15px; font-weight: 500;
  color: #f8fafc; cursor: pointer;
  transition: all 0.2s ease;
  display: flex; align-items: center; justify-content: center; gap: 12px;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  }
  &:disabled { opacity: 0.7; cursor: not-allowed; }
`;

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 10px; padding: 12px 16px;
  color: #f87171; font-size: 13px;
  margin-bottom: 20px; text-align: center;
`;

const SecurityNote = styled.p`
  font-size: 12px; color: #64748b; text-align: center;
  margin-top: 24px; display: flex; align-items: center;
  justify-content: center; gap: 6px;
  svg { font-size: 14px; }
`;

const LoginPage = () => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError("");
    try {
      await signInWithPopup(authenticate, googleProvider);
      // AuthContext's onAuthStateChanged will handle the rest
    } catch (err) {
      if (err.code === "auth/popup-closed-by-user") {
        setError("");
      } else if (err.code === "auth/unauthorized-domain") {
        setError("This domain is not authorized for sign-in. Check Firebase console.");
      } else {
        setError("Failed to sign in with Google. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <BackgroundOrb />
      <BackgroundOrb />
      <BackgroundOrb />

      <LeftSection>
        <BrandSection>
          <LogoWrapper>
            <LogoIcon>G</LogoIcon>
            <BrandName>Growezy</BrandName>
          </LogoWrapper>
          <TagLine>
            Manage your business operations, track settlements, and grow your revenue with our powerful admin dashboard.
          </TagLine>
        </BrandSection>
      </LeftSection>

      <RightSection>
        <LoginCard>
          <CardTitle>Welcome back</CardTitle>
          <CardSubtitle>Sign in with your admin Google account</CardSubtitle>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <GoogleButton onClick={handleGoogleLogin} disabled={loading}>
            {loading ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              <>
                <GoogleIcon />
                Continue with Google
              </>
            )}
          </GoogleButton>

          <SecurityNote>
            <Lock />
            Only authorized admin accounts can sign in
          </SecurityNote>
        </LoginCard>
      </RightSection>
    </PageContainer>
  );
};

export default LoginPage;
